import { useEventCreateMeetingTimeSeries, useEventDeleteMeetingTimeSeries } from "@crowdcoursing/api/openapi";
import { useCallback, useState } from "react";

import { useToast } from "./useToast";

export interface SeriesChange {
  added: { name: string | null; description: string | null }[];
  removed: { id: string | null }[];
}

interface UseEventSeriesProps {
  eventId: string;
  refetchEvent: () => Promise<unknown>;
}

export const useEventSeries = ({ eventId, refetchEvent }: UseEventSeriesProps) => {
  const [isSeriesDialogOpen, setIsSeriesDialogOpen] = useState(false);
  const toast = useToast();

  const { mutateAsync: createSeries } = useEventCreateMeetingTimeSeries();
  const { mutateAsync: deleteSeries } = useEventDeleteMeetingTimeSeries();

  const handleManageSeries = useCallback(() => {
    setIsSeriesDialogOpen(true);
  }, []);

  const handleSaveSeries = useCallback(
    async (changes: SeriesChange) => {
      // Create new series
      if (changes.added.length > 0) {
        await Promise.all(
          changes.added.map((s) =>
            createSeries(
              {
                body: {
                  seriesDescription: s.description || "",
                  seriesName: s.name || "",
                },
                pathParams: { eventId },
              },
              {
                onError: (error) => {
                  console.error("Failed to create series:", error);
                  toast.error(`Failed to create series "${s.name}". Please try again.`);
                },
                onSuccess: () => {
                  toast.success(`Successfully created series "${s.name}"!`);
                },
              },
            ),
          ),
        );
      }

      // Delete removed series
      const seriesToDelete = changes.removed.filter((s) => s.id);
      if (seriesToDelete.length > 0) {
        await Promise.all(
          seriesToDelete.map((s) =>
            deleteSeries(
              {
                pathParams: {
                  eventId,
                  meetingTimeSeriesId: s.id || "",
                },
              },
              {
                onError: (error) => {
                  console.error("Failed to delete series:", error);
                  toast.error("Failed to delete series. Please try again.");
                },
                onSuccess: () => {
                  toast.success("Successfully deleted series!");
                },
              },
            ),
          ),
        );
      }

      await refetchEvent();
      setIsSeriesDialogOpen(false);
    },
    [createSeries, deleteSeries, eventId, refetchEvent, toast],
  );

  return {
    handleManageSeries,
    handleSaveSeries,
    isSeriesDialogOpen,
    onCloseSeriesDialog: () => setIsSeriesDialogOpen(false),
  };
};
